import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchScan } from '../../actions/scan';
import HASHTAG_FILTER_CONFIG from '../../constants/hashtagFilterConfig';
import { getScansForSelection } from '../../constants/hashtagScanMap';
import aiAssistantFabIcon from '../../icons/Icon-AI-Assistant-FAB.svg';
import closeIcon from '../../icons/Icon-AI-Scanner-Close.svg';
import {
  filterSelectedHashtags,
  getRequiredScans
} from '../../utils/hashtagFilterEngine';
import { useFeatureToggle } from '../../utils/hooks/useFeatureToggle';
import useLocalize from '../../utils/hooks/useLocalize';
import useOnboardingSeen from '../../utils/hooks/useOnboardingSeen';
import AiScannerCoachMark from './AiScannerCoachMark';
import HashtagChips from './HashtagChips';

const FAB_BOTTOM_OFFSET = 34;
const MOBILE_FOOTER_ID = 'gnavi-mobile-footer';
const SHEET_PEEK = 44; // visible height when collapsed (px)
const SHEET_TOP_MARGIN = 40; // gap from overlay top when expanded (px)

/**
 * AiScannerContainer Component
 *
 * Renders a fixed floating "AI アシスタント" button at the bottom-right of the
 * Scanner page at the smartphone breakpoint. Tapping the button opens the AI
 * Scanner chat overlay. The overlay can be dismissed via the ✕ icon at the
 * top-right.
 *
 * While the footer is not in view the button stays fixed at the bottom of the
 * viewport (bottom: 34px via CSS). Once the footer scrolls into view the
 * `bottom` value is increased so the button maintains the same 34px gap from
 * the top of the footer instead of from the viewport edge.
 *
 * On the user's first open a two-step onboarding coach mark (see
 * {@link AiScannerCoachMark}) is layered on top of the main blank overlay.
 * The "seen" flag is persisted per user via {@link useOnboardingSeen} and is
 * only recorded when the user completes the onboarding (taps "AI Chatbot を
 * はじめる") or closes the overlay via ✕. Leaving mid-flow does not record
 * the flag, so the onboarding resumes from step 1 on the next visit.
 *
 * @returns {JSX.Element} The AI Scanner FAB and, when open, the overlay.
 */
export const AiScannerContainer = () => {
  const { isFeatureEnabled } = useFeatureToggle('aiScannerPopsicleEnabled');
  const { hasSeen, markSeen, userId } = useOnboardingSeen();
  const localize = useLocalize();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [fabBottomOverride, setFabBottomOverride] = useState(null);
  // Locally track whether the coach mark is showing for the CURRENT open
  // session. This lets the "seen" flag be flipped in localStorage before we
  // unmount without immediately re-rendering the main chat mid-transition.
  const [showCoachMark, setShowCoachMark] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [sheetOffset, setSheetOffset] = useState(null); // px from top while dragging; null = snapped
  const [sheetDragging, setSheetDragging] = useState(false);
  const sheetRef = useRef(null);
  const sheetExpandedRef = useRef(false);
  const dragRef = useRef({
    active: false, startY: 0, startOffset: 0, lastY: 0, lastT: 0, velocity: 0
  });

  // Helper to keep the expanded ref in sync for pointer event handlers
  const updateSheetExpanded = useCallback((value) => {
    sheetExpandedRef.current = value;
    setSheetExpanded(value);
  }, []);

  // Reset the overlay when the active user changes (e.g., switching between
  // user sessions on the same device without a full page reload) so state
  // from the previous user's session does not carry over.
  useEffect(() => {
    setIsOpen(false);
    setShowCoachMark(false);
    updateSheetExpanded(false);
    setSheetOffset(null);
    setSheetDragging(false);
  }, [userId, updateSheetExpanded]);

  useEffect(() => {
    if (!isFeatureEnabled) {
      return;
    }

    let rafId = null;

    const updateFabPosition = () => {
      const footer = document.getElementById(MOBILE_FOOTER_ID);
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      if (footerRect.top < window.innerHeight) {
        setFabBottomOverride(window.innerHeight - footerRect.top + FAB_BOTTOM_OFFSET);
      } else {
        setFabBottomOverride(null);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        updateFabPosition();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateFabPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isFeatureEnabled]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    // Show the coach mark only for users who have not yet seen it on this
    // device. Returning users skip straight to the chat.
    setShowCoachMark(!hasSeen);
  }, [hasSeen]);

  const handleClose = useCallback(() => {
    // ✕ during onboarding counts as a completion for "seen" tracking, per
    // FR-007 / Rule 4. If the coach mark was already dismissed this session,
    // don't call markSeen again — the hook itself is idempotent, but skipping
    // the call keeps container behavior explicit and testable.
    if (showCoachMark) {
      markSeen();
    }
    setShowCoachMark(false);
    setIsOpen(false);
  }, [markSeen, showCoachMark]);

  const handleCoachMarkComplete = useCallback(() => {
    markSeen();
    setShowCoachMark(false);
  }, [markSeen]);

  // --- Bottom sheet snap positions (relative to overlay top) ---
  const getSheetSnaps = useCallback(() => {
    const h = sheetRef.current?.parentElement?.clientHeight || window.innerHeight;
    return { open: SHEET_TOP_MARGIN, closed: h - SHEET_PEEK };
  }, []);

  // Tapping the hashtag link programmatically expands the sheet.
  const handleExpandSheet = useCallback(() => {
    updateSheetExpanded(true);
    setSheetOffset(null);
  }, [updateSheetExpanded]);

  // STORY-007: Fire de-duplicated scan requests when hashtag selection changes.
  // Track in-flight scans to avoid duplicate concurrent requests for the same scan.
  // User/token validation is handled inside fetchScan (see actions/scan.js).
  const pendingScans = useRef(new Set());

  // STORY-008: Selection state feeds the live-count derivation below.
  // `pendingTick` is a render trigger bumped whenever `pendingScans` mutates,
  // so the memo re-evaluates as fetches start and complete.
  const [selectedHashtagIds, setSelectedHashtagIds] = useState(() => new Set());
  const [pendingTick, setPendingTick] = useState(0); // re-render trigger for pendingScans changes

  const handleHashtagSelectionChange = useCallback(
    (selectedIds) => {
      setSelectedHashtagIds(new Set(selectedIds));

      // Fire scan requests immediately (STORY-007 behavior preserved).
      const scanNames = getScansForSelection(selectedIds);
      scanNames.forEach((scanName) => {
        if (pendingScans.current.has(scanName)) return;
        pendingScans.current.add(scanName);
        setPendingTick((t) => t + 1);
        const result = dispatch(fetchScan(scanName));
        const cleanup = () => {
          pendingScans.current.delete(scanName);
          // Bump the tick so the count derivation re-runs once this scan
          // resolves. The derivation always reads the latest selection, so
          // responses from a superseded selection simply populate the cache.
          setPendingTick((t) => t + 1);
        };
        if (result && typeof result.then === 'function') {
          result.finally(cleanup);
        } else {
          cleanup();
        }
      });
    },
    [dispatch]
  );

  // STORY-008: memoized derivation of live count / loading / error.
  //
  // We deliberately do NOT wire this through a reselect selector: the
  // `pendingScans` Set is held in a ref and mutated in place, so its
  // reference identity never changes across renders. That would trip up
  // reselect's referential-equality memoization and could return a stale
  // `{ isLoading: true }` even after fetches complete. Reading the store
  // slices with plain `useSelector` and deriving here with `useMemo` keeps
  // the data flow explicit and re-evaluates on every dependency change.
  const scanData = useSelector(
    (state) => (state && state.scans && state.scans.scanData) || null
  );
  const scanErrors = useSelector(
    (state) => (state && state.scans && state.scans.scanErrors) || null
  );

  const filterResult = useMemo(() => {
    const empty = {
      count: 0,
      symbols: [],
      isLoading: false,
      hasError: false,
      missingScans: []
    };
    if (!selectedHashtagIds || selectedHashtagIds.size === 0) return empty;

    const data = scanData || {};
    const errors = scanErrors || {};
    const required = getRequiredScans(
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );

    if (required.some((s) => errors[s.toLowerCase()])) {
      return {
        count: null,
        symbols: [],
        isLoading: false,
        hasError: true,
        missingScans: []
      };
    }

    const missing = required.filter((s) => !data[s.toLowerCase()]);
    const stillPending = required.some((s) => pendingScans.current.has(s));
    if (missing.length > 0 || stillPending) {
      return {
        count: null,
        symbols: [],
        isLoading: true,
        hasError: false,
        missingScans: missing
      };
    }

    const symbols = filterSelectedHashtags(
      data,
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );
    return {
      count: symbols.length,
      symbols,
      isLoading: false,
      hasError: false,
      missingScans: []
    };
    // `pendingTick` is intentionally in the deps: it's the render-trigger
    // bumped whenever `pendingScans.current` mutates so the memo recomputes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanData, scanErrors, selectedHashtagIds, pendingTick]);

  // --- Pointer-event drag handlers for the bottom sheet ---
  const handleSheetPointerDown = useCallback((e) => {
    const { open, closed } = getSheetSnaps();
    dragRef.current = {
      active: true,
      startY: e.clientY,
      startOffset: sheetExpandedRef.current ? open : closed,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
    };
    setSheetDragging(true);
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, [getSheetSnaps]);

  const handleSheetPointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const { open, closed } = getSheetSnaps();
    const now = performance.now();
    const dt = now - dragRef.current.lastT || 16;
    dragRef.current.velocity = (e.clientY - dragRef.current.lastY) / dt;
    dragRef.current.lastY = e.clientY;
    dragRef.current.lastT = now;

    let next = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    next = Math.max(open, Math.min(closed, next));
    setSheetOffset(next);
  }, [getSheetSnaps]);

  const handleSheetPointerUp = useCallback((e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setSheetDragging(false);

    const { open, closed } = getSheetSnaps();
    const v = dragRef.current.velocity;
    const traveled = Math.abs(e.clientY - dragRef.current.startY);
    // Compute current position from drag data to avoid stale closure on sheetOffset
    const current = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    const wasExpanded = sheetExpandedRef.current;

    let willExpand;
    if (traveled < 6) {
      // Barely moved — treat as a tap and toggle.
      willExpand = !wasExpanded;
    } else if (Math.abs(v) > 0.5) {
      // Fast flick wins regardless of position.
      willExpand = v < 0;
    } else {
      // Slow drag — snap to whichever half is closer.
      willExpand = current < (open + closed) / 2;
    }

    updateSheetExpanded(willExpand);
    setSheetOffset(null);
  }, [getSheetSnaps, updateSheetExpanded]);

  // Escape key collapses the sheet
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && sheetExpanded) {
        updateSheetExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, sheetExpanded, updateSheetExpanded]);

  // Move focus into the sheet when it expands so keyboard/screen-reader users
  // are aware the content is available. The sheet container itself receives
  // focus (tabIndex={-1} below).
  useEffect(() => {
    if (sheetExpanded) {
      sheetRef.current?.focus();
    }
  }, [sheetExpanded]);

  if (!isFeatureEnabled) {
    return null;
  }

  // Compute bottom sheet translateY
  const { open: snapOpen, closed: snapClosed } = getSheetSnaps();
  const sheetY = sheetOffset ?? (sheetExpanded ? snapOpen : snapClosed);
  const sheetStyle = {
    transform: `translateY(${sheetY}px)`,
    transition: sheetDragging ? 'none' : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
  };

  return (
    <div className="ai-scanner-container">
      <button
        className="ai-scanner-fab"
        style={fabBottomOverride !== null ? { bottom: `${fabBottomOverride}px` } : 
undefined}
        onClick={handleOpen}
        aria-label="AIアシスタントを開く"
        aria-expanded={isOpen}
      >
        <img
          src={aiAssistantFabIcon}
          alt=""
          aria-hidden="true"
          width="68"
          height="68"
        />
      </button>
      {isOpen && (
        <div
          className="ai-scanner-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="AIスキャナー"
        >
          <button
            className="ai-scanner-overlay-close"
            onClick={handleClose}
            aria-label="閉じる"
          >
            <img
              src={closeIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
          <div className="ai-scanner-overlay-content">
            <p className="ai-scanner-overlay-greeting">
              {localize('ai.scanner.greeting')}
            </p>
            <button
              className="ai-scanner-overlay-hashtag-link"
              onClick={handleExpandSheet}
              type="button"
            >
              {localize('ai.scanner.hashtag.link')}
            </button>
          </div>
          <div
            className={`ai-scanner-overlay-swipe-area${
              sheetExpanded ? ' ai-scanner-overlay-swipe-area--expanded' : ''
            }`}
            ref={sheetRef}
            style={{ '--sheet-top-margin': `${SHEET_TOP_MARGIN}px`, ...sheetStyle }}
            tabIndex={-1}
            role={sheetExpanded ? 'region' : undefined}
            aria-labelledby={sheetExpanded ? 'ai-scanner-sheet-title' : undefined}
          >
            <div
              className="ai-scanner-overlay-swipe-area-handle"
              onPointerDown={handleSheetPointerDown}
              onPointerMove={handleSheetPointerMove}
              onPointerUp={handleSheetPointerUp}
              onPointerCancel={handleSheetPointerUp}
              role="button"
              tabIndex={0}
              aria-expanded={sheetExpanded}
              aria-label={sheetExpanded ? localize('button.label.close') : localize('ai.scanner.hashtag.modal.open')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  updateSheetExpanded(!sheetExpanded);
                }
              }}
            >
              <div className="ai-scanner-overlay-swipe-area-grabber" />
            </div>
            <div
              className="ai-scanner-overlay-swipe-area-body"
              aria-hidden={!sheetExpanded}
              style={{
                opacity: sheetExpanded || sheetOffset !== null ? 1 : 0,
                overflowY: 'hidden',
              }}
            >
              <div className="ai-scanner-overlay-swipe-area-content">
                <p id="ai-scanner-sheet-title" className="ai-scanner-overlay-swipe-area-title">
                  {localize('ai.scanner.hashtag.modal.title')}
                </p>
                <HashtagChips
                  onSelectionChange={handleHashtagSelectionChange}
                  matchCount={filterResult.count}
                  isLoading={filterResult.isLoading}
                  hasError={filterResult.hasError}
                />
              </div>
            </div>
          </div>
          {showCoachMark && (
            <AiScannerCoachMark onComplete={handleCoachMarkComplete} />
          )}
        </div>
      )}
    </div>
  );
};

export default AiScannerContainer;
